import { motion } from 'framer-motion';
import { MapPin, Wrench, Anchor } from 'lucide-react';
import { ReactNode } from 'react';

interface PhaseProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
  items: string[];
}

const Phase = ({ icon, title, subtitle, items }: PhaseProps) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ once: true }}
    className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow"
  >
    <div className="flex items-center gap-4 mb-6">
      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
        {icon}
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>
    </div>
    <ul className="space-y-3">
      {items.map((item, idx) => (
        <li key={idx} className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/20 text-primary text-xs font-bold mt-0.5 flex-shrink-0">
            ✓
          </span>
          <span className="text-gray-700">{item}</span>
        </li>
      ))}
    </ul>
  </motion.div>
);

const MethodPhases = () => {
  const phases: PhaseProps[] = [
    {
      icon: <MapPin className="h-6 w-6" />,
      title: 'Cadrer',
      subtitle: '1 à 2 semaines',
      items: [
        'Diagnostic de maturité IA',
        'Cartographie des données',
        'Choix du cas d\'usage',
        'Feuille de route IA'
      ]
    },
    {
      icon: <Wrench className="h-6 w-6" />,
      title: 'Livrer',
      subtitle: '4 à 8 semaines',
      items: [
        'Conception de la solution',
        'Formation des équipes',
        'Développement',
        'Mise en production'
      ]
    },
    {
      icon: <Anchor className="h-6 w-6" />,
      title: 'Ancrer',
      subtitle: 'En continu',
      items: [
        'Suivi des KPI',
        'Coaching utilisateurs',
        'Itération',
        'Extension à d\'autres processus'
      ]
    }
  ];

  return (
    <section className="section-padding bg-gray-50">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="heading-lg mb-4">Notre méthode en 3 phases</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Cadrer, livrer, ancrer: aucune étape n'est sautée
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {phases.map((phase, idx) => (
            <Phase key={idx} {...phase} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default MethodPhases;
